import { ArrayUtils, isString } from '@cs/core';


export class DataTransforms {
	/**
	 * Groups datasources by specified property and aggregates their values.
	 * 
	 * Used primarily in hover popover components to display aggregated datasource breakdowns.
	 * Groups all cellData entries by the specified property and sums their values within each group.
	 * 
	 * @param cellData Array of datasource items to group and aggregate
	 * @param groupByProperty Property name to group by (default: 'iddatasource'). 
	 *                        Can be set via CellBehavior.datasourceGroupProperty from grid rules.
	 *                        Common values: 'iddatasource', 'idscenario'
	 * @returns Array of grouped datasources with aggregated values and preserved metadata
	 * 
	 * @example
	 * // Group by datasource (default)
	 * DataTransforms.mergeSameDesignations(cellData); 
	 * // Result: [{ iddatasource: 1, value: 150 }, { iddatasource: 2, value: 200 }]
	 * 
	 * // Group by scenario (server override)
	 * DataTransforms.mergeSameDesignations(cellData, 'idscenario');
	 * // Result: [{ idscenario: 'Budget', value: 350 }, { idscenario: 'Forecast', value: 200 }]
	 */
	/**
	 * Groups datasources by a property while preserving individual datasources within each group.
	 * Creates a hierarchical structure: groups with nested datasources.
	 * 
	 * @param cellData Array of datasource items to group
	 * @param groupByProperty Property name to group by (default: 'iddatasource').
	 *                        Can be set via CellBehavior.datasourceGroupProperty from grid rules.
	 *                        Common values: 'iddatasource', 'idscenario'
	 * @returns Array of group objects, each containing:
	 *          - [groupByProperty]: the group key value
	 *          - value: aggregated sum of all datasources in the group
	 *          - datasources: array of individual datasources in this group
	 *          - metadata from first datasource in group
	 * 
	 * @example
	 * // Group by scenario, preserving individual datasources
	 * const data = [
	 *   { idscenario: 2, iddatasource: 1, value: 38.99 },
	 *   { idscenario: 2, iddatasource: 2, value: 0 },
	 *   { idscenario: 2, iddatasource: 184, value: 11.01 }
	 * ];
	 * DataTransforms.groupDatasourcesByProperty(data, 'idscenario');
	 * // Result: [{
	 * //   idscenario: 2,
	 * //   value: 50.003,  // aggregated
	 * //   datasources: [
	 * //     { iddatasource: 1, value: 38.99 },
	 * //     { iddatasource: 2, value: 0 },
	 * //     { iddatasource: 184, value: 11.01 }
	 * //   ]
	 * // }]
	 */
	static groupDatasourcesByProperty(cellData, groupByProperty: string = 'iddatasource') {
		const grouped = ArrayUtils.groupBy<any>(groupByProperty, cellData);
		const groupedResult = [];

		for (const groupName of Object.keys(grouped)) {
			const foundDataSources = grouped[groupName];
			
			// Calculate aggregated value for the group
			let aggregatedValue = 0;
			foundDataSources.forEach(item => {
				aggregatedValue += isString(item.value) || isNaN(item.value) ? 0 : item.value;
			});

			// Create group object with individual datasources preserved
			const groupObject: any = {
				[groupByProperty]: foundDataSources[0][groupByProperty],
				value: aggregatedValue,
				datasources: foundDataSources.map(item => ({
					...item,
					value: isString(item.value) || isNaN(item.value) ? '' : item.value
				}))
			};

			// Preserve all metadata properties from first datasource
			Object.keys(foundDataSources[0]).forEach(key => {
				if (key !== 'value' && !(key in groupObject)) {
					groupObject[key] = foundDataSources[0][key];
				}
			});

			groupedResult.push(groupObject);
		}
		return groupedResult;
	}

	static mergeSameDesignations(cellData, groupByProperty: string = 'iddatasource') {
		const grouped           = ArrayUtils.groupBy<any>(groupByProperty, cellData);
		const mergedDataSources = [];

		for (const groupName of Object.keys(grouped)) {
			const foundDataSources = grouped[groupName];
			let output;

			if (foundDataSources.length > 1) {
				output = foundDataSources.reduce((sum, item) => {
					const computedObject = sum || {[groupByProperty]: item[groupByProperty], value: item.value};
					computedObject.value += isString(item.value) || isNaN(item.value) ? 0 : item.value;
					// Preserve all properties from first item for metadata
					if (!sum) {
						Object.keys(item).forEach(key => {
							if (key !== 'value' && !(key in computedObject)) {
								computedObject[key] = item[key];
							}
						});
					}
					return computedObject;
				});

				console.log(output);
			} else {
				const item = foundDataSources[0];
				item.value = isString(item.value) || isNaN(item.value) ? '' : item.value;
				output     = item;
			}

			mergedDataSources.push(output);
		}
		return mergedDataSources;
	}
}
